/**
 * Action handlers for onboarding steps
 * Centralizes all step-specific action logic
 */

import type {
  IntakeDocsMetadata,
  IntegrationMetadata,
  OnboardingStepId,
  OnboardingStepItem,
} from "../types";
import { createSampleClient } from "@pages/Onboarding/api/taxOfficeApi";
import { showNotification } from "@mantine/notifications";
import {
  openExtensionIntegrationOnboarding,
  openExtensionWithClient,
  openExtensionToClientCreation,
} from "@/utils/extensionClient";
import {
  markCustomizeOneClickVisited,
  markIntakeDocsVisited,
  markReferralVisited,
  markWizzyVisited,
} from "@pages/SmartwizLiteHome/api/onboardingStepsApi";
import { openNewTab } from "@/utils/browserUtils";

export interface StepActionContext {
  navigate: (route: string) => void;
  setActiveStep: (stepId: OnboardingStepId | null) => void;
  refreshSteps: () => Promise<void>;
  steps?: OnboardingStepItem[];
}

/**
 * Execute the action for a specific onboarding step
 */
export const executeStepAction = async (
  stepId: OnboardingStepId,
  context: StepActionContext
): Promise<void> => {
  const { setActiveStep, refreshSteps } = context;

  switch (stepId) {
    case "SAMPLE_CLIENT":
      // Create sample client and open in extension
      try {
        const response = await createSampleClient();
        console.log("✅ Sample client created:", response.clients);

        if (!response.clients || response.clients.length === 0) {
          throw new Error("No sample clients were created");
        }

        const firstClient = response.clients[0];
        const success = await openExtensionWithClient(firstClient.clientId);

        if (success) {
          // Refresh steps to update completion status
          await refreshSteps();
        } else {
          showNotification({
            title: "Warning",
            message:
              "Sidepanel may not have opened properly. Please try manually.",
            color: "yellow",
          });
        }

        // Navigate to sample client demo page
        setActiveStep("SAMPLE_CLIENT");
      } catch (error) {
        console.error("Failed to create sample client:", error);
        showNotification({
          title: "Error",
          message: "Failed to create sample client. Please try again.",
          color: "red",
        });
      }
      break;

    case "INTEGRATION":
      // Open extension and navigate to integration onboarding page
      try {
        const { steps } = context;
        const integrationStep = steps?.find((s) => s.id === "INTEGRATION");
        const metadata = integrationStep?.metadata as
          IntegrationMetadata | undefined;

        if (!metadata?.integrationName) {
          console.error("No integration name found in metadata");
          showNotification({
            title: "Error",
            message:
              "Integration information not found. Please contact support.",
            color: "red",
          });
          return;
        }

        const success = await openExtensionIntegrationOnboarding(
          metadata.integrationName
        );

        if (success) {
          // Refresh steps to update completion status after onboarding
          setTimeout(() => {
            refreshSteps();
          }, 2000);
        } else {
          showNotification({
            title: "Extension Not Available",
            message:
              "Please install the SmartWiz extension to connect integrations.",
            color: "yellow",
          });
          // Stay on home page - user can retry by clicking the step card again
        }
      } catch (error) {
        console.error("Failed to open integration onboarding:", error);
        showNotification({
          title: "Error",
          message: "Failed to start integration setup. Please try again.",
          color: "red",
        });
        // Stay on home page - user can retry by clicking the step card again
      }
      break;

    case "IMPORT_CLIENT":
      // Open extension directly to client creation page
      try {
        const success = await openExtensionToClientCreation();

        if (success) {
          // Refresh steps to update completion status after import
          setTimeout(() => {
            refreshSteps();
          }, 2000);
        } else {
          showNotification({
            title: "Extension Not Available",
            message: "Please install the SmartWiz extension to import clients.",
            color: "yellow",
          });
          // Stay on home page - user can retry by clicking the step card again
        }
      } catch (error) {
        console.error("Failed to open extension to client creation:", error);
        showNotification({
          title: "Error",
          message: "Failed to open extension. Please try again.",
          color: "red",
        });
        // Stay on home page - user can retry by clicking the step card again
      }
      break;

    case "CUSTOMIZE_ONECLICK":
      // Mark customize one-click page as visited and show settings
      try {
        // Mark as visited on backend
        await markCustomizeOneClickVisited();

        // Show OneClick settings in onboarding context
        setActiveStep("CUSTOMIZE_ONECLICK");

        // Refresh steps to update UI
        await refreshSteps();
      } catch (error) {
        console.error("Failed to mark Customize OneClick as visited:", error);
        // Still show the page even if tracking fails
        setActiveStep("CUSTOMIZE_ONECLICK");
      }
      break;

    case "WIZZY_DO":
      // Open Notion page in new tab and mark as visited
      try {
        openNewTab("https://smartwiz-io.notion.site/what-can-wizzy-do");

        // Mark as visited on backend
        await markWizzyVisited();

        // Refresh steps to update UI
        await refreshSteps();

        showNotification({
          title: "Learning about Wizzy",
          message: "Opened in a new tab. Come back when you're done!",
          color: "blue",
        });
      } catch (error) {
        console.error("Failed to mark Wizzy as visited:", error);
      }
      break;

    case "TEAM_MEMBERS":
      // Show team management in onboarding context
      setActiveStep("TEAM_MEMBERS");
      break;

    case "REFERRAL":
      // Mark referral page as visited and show referral settings
      try {
        // Mark as visited on backend
        await markReferralVisited();

        // Show referral settings in onboarding context
        setActiveStep("REFERRAL");

        // Refresh steps to update UI
        await refreshSteps();
      } catch (error) {
        console.error("Failed to mark Referral as visited:", error);
        // Still show the page even if tracking fails
        setActiveStep("REFERRAL");
      }
      break;

    case "INTAKE_DOCS":
      // Open intake documentation in new tab and mark as visited
      try {
        const { steps } = context;
        const intakeDocsStep = steps?.find((s) => s.id === "INTAKE_DOCS");
        const metadata = intakeDocsStep?.metadata as
          IntakeDocsMetadata | undefined;

        if (!metadata?.docUrl) {
          console.error("No documentation URL found in metadata");
          showNotification({
            title: "Error",
            message: "Documentation URL not found. Please contact support.",
            color: "red",
          });
          return;
        }

        // Build the full URL
        const docUrl = `https://hub.smartwiz.ai/${metadata.docUrl}`;

        // Open documentation in new tab
        openNewTab(docUrl);

        // Mark as visited on backend
        await markIntakeDocsVisited(metadata.docUrl);

        // Refresh steps to update UI
        await refreshSteps();

        const softwareName = metadata.intakeSoftware
          .split(" ")
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        showNotification({
          title: "Opening Documentation",
          message: `Opened ${softwareName} integration guide in a new tab.`,
          color: "blue",
        });
      } catch (error) {
        console.error("Failed to mark intake docs as visited:", error);
      }
      break;

    default:
      console.warn(`No action defined for step: ${stepId}`);
  }
};

/**
 * Check if a step action requires navigation away from the page
 */
export const isNavigatingAction = (_stepId: OnboardingStepId): boolean => {
  // No steps navigate away anymore - they all use setActiveStep
  return false;
};

/**
 * Check if a step action opens an external resource
 */
export const isExternalAction = (stepId: OnboardingStepId): boolean => {
  return ["WIZZY_DO", "INTAKE_DOCS"].includes(stepId);
};
